import { useCallback, useState } from "react";

/**
 * Bulk-selection UX: select mode toggle, per-row checked state, select-all
 * helpers, and the cascade rule that deleting a parent also deletes all its
 * currently-loaded children (so the user can't orphan subtasks).
 */
export default function useBulkSelection({ allTasks, filteredTasks }) {
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const exit = useCallback(() => {
    setSelectMode(false);
    setSelectedIds(new Set());
  }, []);

  const toggleMode = useCallback(() => {
    setSelectMode((v) => {
      if (v) setSelectedIds(new Set());
      return !v;
    });
  }, []);

  const toggleRow = useCallback((taskId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(
    (shouldSelect) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (shouldSelect) {
          for (const t of filteredTasks) next.add(t.id);
        } else {
          for (const t of filteredTasks) next.delete(t.id);
        }
        return next;
      });
    },
    [filteredTasks],
  );

  /**
   * Expands the current selection to include subtasks of any selected parent,
   * so the caller can issue one cascading delete batch. Pure — never mutates
   * component state.
   */
  const expandedSelection = useCallback(() => {
    const ids = new Set(selectedIds);
    for (const t of allTasks) {
      if (t.parent_id && ids.has(t.parent_id)) ids.add(t.id);
    }
    return ids;
  }, [allTasks, selectedIds]);

  return {
    selectMode,
    selectedIds,
    toggleMode,
    toggleRow,
    toggleSelectAll,
    exit,
    expandedSelection,
  };
}
